/**
 * Theming props for the Opportunities Widget.
 * These map to the CSS custom properties used internally.
 */
export interface OpportunitiesThemingProps {
  // Panel theming
  /** Panel gradient start opacity (0-1) */
  oppPanelStart?: string;
  /** Panel gradient mid opacity (0-1) */
  oppPanelMid?: string;
  /** Panel gradient end opacity (0-1) */
  oppPanelEnd?: string;
  /** Panel color as RGB, e.g. "0, 0, 0" */
  oppPanelColor?: string;
  /** Panel shadow CSS value */
  oppPanelShadow?: string;
  /** Panel backdrop blur, e.g. "2px" */
  oppPanelBlur?: string;
  /** Panel border radius, e.g. "10px" */
  oppPanelRadius?: string;
  /** Panel padding CSS value */
  oppPanelPadding?: string;
  /** Panel max width CSS value */
  oppPanelMaxWidth?: string;

  // Card theming
  /** Card border radius, e.g. "12px" */
  oppCardRadius?: string;
  /** Card shadow CSS value */
  oppCardShadow?: string;
  /** Card border CSS value */
  oppCardBorder?: string;
  /** Card minimum height, e.g. "240px" */
  oppCardMinHeight?: string;

  // Title typography
  /** Title font size, e.g. "clamp(18px, 2.6vmin, 36px)" */
  oppTitleSize?: string;
  /** Title text color */
  oppTitleColor?: string;
  /** Title font weight */
  oppTitleWeight?: string;
  /** Title text shadow */
  oppTitleShadow?: string;
  /** Title font family */
  oppTitleFontFamily?: string;
  /** Title letter spacing */
  oppTitleLetterSpacing?: string;

  // Description typography
  /** Description font size */
  oppDescriptionSize?: string;
  /** Description text color */
  oppDescriptionColor?: string;
  /** Description text shadow */
  oppDescriptionShadow?: string;
  /** Description font family */
  oppDescriptionFontFamily?: string;
  /** Description line height */
  oppDescriptionLineHeight?: string;

  // Expires typography
  /** Expires text font size */
  oppExpiresSize?: string;
  /** Expires text color */
  oppExpiresColor?: string;
  /** Expires text shadow */
  oppExpiresShadow?: string;

  // Button theming
  /** Button background color */
  oppButtonBg?: string;
  /** Button text color */
  oppButtonColor?: string;
  /** Button border radius */
  oppButtonRadius?: string;
  /** Button shadow */
  oppButtonShadow?: string;

  // Animation theming
  /** Transition duration, e.g. "0.3s" */
  oppTransitionDuration?: string;
  /** Hover scale factor, e.g. "1.02" */
  oppAnimationScale?: string;
}

/**
 * Core configuration props for the Opportunities Widget.
 */
export interface OpportunitiesWidgetProps extends OpportunitiesThemingProps {
  /** Base URL of the Legion application (required) */
  baseUrl: string;
  /** Number of opportunities to display (1-20). Default: 5 */
  count?: number;
  /** Layout direction. Default: "horizontal" */
  layout?: "horizontal" | "vertical";
  /** Comma-separated list of tags to filter by */
  tags?: string;
  /** Preset theme. Default: "light" */
  theme?: "light" | "dark" | "minimal" | "bold" | "corporate" | "vibrant";
  /** Widget height: "auto", "100%", or specific value (e.g. "400px"). Default: "auto" */
  height?: string;
  /** Optional API key for authentication */
  apiKey?: string;
  /** Additional CSS class name for the container */
  className?: string;
  /** Additional inline styles for the container */
  style?: React.CSSProperties;
  /** Callback when the widget iframe has loaded */
  onLoad?: (detail: {
    widget: string;
    count: number;
    layout: string;
    tags: string;
  }) => void;
  /** Callback when the widget iframe fails to load */
  onError?: (message: string) => void;
}

/**
 * Mapping from camelCase prop names to the kebab-case query parameter names
 * used by the widget iframe URL.
 */
export const THEMING_PROP_TO_PARAM: Record<
  keyof OpportunitiesThemingProps,
  string
> = {
  oppPanelStart: "oppPanelStart",
  oppPanelMid: "oppPanelMid",
  oppPanelEnd: "oppPanelEnd",
  oppPanelColor: "oppPanelColor",
  oppPanelShadow: "oppPanelShadow",
  oppPanelBlur: "oppPanelBlur",
  oppPanelRadius: "oppPanelRadius",
  oppPanelPadding: "oppPanelPadding",
  oppPanelMaxWidth: "oppPanelMaxWidth",
  oppCardRadius: "oppCardRadius",
  oppCardShadow: "oppCardShadow",
  oppCardBorder: "oppCardBorder",
  oppCardMinHeight: "oppCardMinHeight",
  oppTitleSize: "oppTitleSize",
  oppTitleColor: "oppTitleColor",
  oppTitleWeight: "oppTitleWeight",
  oppTitleShadow: "oppTitleShadow",
  oppTitleFontFamily: "oppTitleFontFamily",
  oppTitleLetterSpacing: "oppTitleLetterSpacing",
  oppDescriptionSize: "oppDescriptionSize",
  oppDescriptionColor: "oppDescriptionColor",
  oppDescriptionShadow: "oppDescriptionShadow",
  oppDescriptionFontFamily: "oppDescriptionFontFamily",
  oppDescriptionLineHeight: "oppDescriptionLineHeight",
  oppExpiresSize: "oppExpiresSize",
  oppExpiresColor: "oppExpiresColor",
  oppExpiresShadow: "oppExpiresShadow",
  oppButtonBg: "oppButtonBg",
  oppButtonColor: "oppButtonColor",
  oppButtonRadius: "oppButtonRadius",
  oppButtonShadow: "oppButtonShadow",
  oppTransitionDuration: "oppTransitionDuration",
  oppAnimationScale: "oppAnimationScale",
};

